// A league's chat, from the screens: one feed for a practice league and a real
// one (PLAN-2026-09-26-chat C3). The drawer, the panel and the unread count all
// read this; it reads the simulator or the API by which league the URL names
// (`useLeagueSource`), never by what happened to answer.
//
// ASKED AGAIN ON A TIMER, because nothing pushes yet (the league room is D10's).
// Every few seconds while the drawer is open, every half minute while it is
// shut — so the chip's count and the notify sound still find somebody's words —
// and not at all while the page is hidden; showing the page again asks at once
// (B-186).
//
// AND ASKS ONLY WHAT CHANGED (2026-10-10). Each answer carries a `mark`, and
// the next ask sends it back as `since`: the answer is the messages written or
// changed after it, laid over what is held by id, where every ask used to read
// the newest hundred whole. The page is read whole the first time a league is
// asked, whenever the drawer opens — what a mark cannot carry, a renamed
// author or a line taken down, is put right when somebody looks — and when the
// server says more changed than a page.
//
// A WRITE WINS OVER AN ASK ALREADY IN FLIGHT. An ask that started before my
// message was sent answers without it, and laying that page down would make
// the message vanish for a beat after it appeared; so an ask that a write has
// overtaken is thrown away and asked again.
//
// THE POST IS KEYED (B-150's rule, `moves.ts`): the same words sent again after
// no answer carry the same `Idempotency-Key` and are the first message, not a
// second one in front of the league.
//
// A DIRECT CONVERSATION (I8) IS A CHANNEL LIKE THE LEAGUE'S, `dm:…`, read and
// written the same way while it is open in the drawer. The list of them, and
// whom I can begin one with, ride the league channel's answers, so the chip's
// count of new ones costs no ask of its own.
//
// THE DEVICE KEEPS EACH CHANNEL'S NEWEST PAGE (B-557, `kept.ts`), so a drawer
// opened with no connection shows the talk it last showed, under the
// sentence saying the chat could not be read. It is drawn only while nothing
// newer is held, and the next answer replaces it whole.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChatMessage as ChatMessageSchema,
  ChatPage,
  ConversationRef as ConversationRefSchema,
  type ChatMessage,
  type ChatReaction,
  type ChatShare,
  type ConversationRef,
  type CreatePollInput,
  type StickerId,
} from '@fsp/domain';
import { chatView } from '@fsp/sim';
import { z } from 'zod';
import { get, messageOf, send } from './api';
import { useMyTeam } from './hooks';
import { GONE, NOT_NOW } from './kept';
import { onDevice } from './keptIdb';
import { useActiveLeague } from './LeaguesProvider';
import { useLeagueSource } from './leagueSource';
import { AttemptKeys, movesChanged } from './moves';
import { useSession } from './session';
import { useSim } from './SimProvider';

/** How often a real league's channel is asked for, open and shut (B-186). */
export const CHAT_ASK_OPEN_MS = 5_000;
export const CHAT_ASK_SHUT_MS = 30_000;

/** The newest page a real league's drawer holds. Older ones wait on CHAT.md's P1. */
const PAGE = 100;

/** A channel's kept page, read back as messages this build knows. */
const KeptItems = z.array(ChatMessageSchema);

export type ChatOutcome = { ok: true } | { ok: false; message: string };

/** Somebody I can begin a conversation with: another team's manager (I8). */
export interface ChatPerson {
  teamId: string;
  teamName: string;
  name: string;
}

/**
 * A player or a game going with the next line, and the words that say it over
 * the box (*Patrick Mahomes · QB · KC*), from whoever attached it
 * (PLAN-2026-10-31-players-and-games-in-chat).
 */
export interface ChatAttachment {
  share: ChatShare;
  label: string;
}

export interface ChatFeed {
  /** Oldest first, as the drawer draws them. */
  messages: ChatMessage[];
  /** `loading` until a real league's first page lands; `failed` keeps what it had. */
  state: 'loading' | 'ready' | 'failed';
  /** Why the last ask failed, to say over what is still shown. */
  problem: string | null;
  /** Say something; `replyTo` answers a line of this channel (C2). */
  post(body: string, replyTo?: string | null): Promise<ChatOutcome>;
  /** Send a sticker, drawn with my team (PLAN-2026-10-30-stickers); it may answer a line too. */
  sticker(sticker: StickerId, replyTo?: string | null): Promise<ChatOutcome>;
  /**
   * Share a player or a game, with my words or none
   * (PLAN-2026-10-31-players-and-games-in-chat): a real league's alone, its
   * channel and its conversations; the practice league has none.
   */
  share?: (share: ChatShare, body: string | null, replyTo?: string | null) => Promise<ChatOutcome>;
  /**
   * Ask the league a poll (PLAN-2026-10-30-polls-in-chat): a real league's
   * channel alone, absent in a conversation and in the practice league,
   * which has nobody else to answer it.
   */
  ask?: (poll: CreatePollInput) => Promise<ChatOutcome>;
  /** My vote in a poll, by its option's place, or null to take it back. */
  vote?: (pollId: string, option: number | null) => Promise<ChatOutcome>;
  react(messageId: string, emoji: ChatReaction, on: boolean): Promise<ChatOutcome>;
  /**
   * The commissioner hides a message, or shows it again (I9, B-449). A real
   * league's alone; the practice league's feed has none.
   */
  hide?: (messageId: string, hidden: boolean) => Promise<ChatOutcome>;
  /**
   * My direct conversations in the league, newest first (I8): a real
   * league's league channel alone; absent where there are none to have.
   */
  conversations?: ConversationRef[];
  /** Whom I can begin one with, by team. Alongside `conversations`. */
  people?: ChatPerson[];
}

const UNREACHABLE =
  'Could not reach the site, so we cannot tell whether that was sent. Look at the chat, then try again.';

/** The active league's channel. `open` is whether somebody is looking at it. */
export function useChatFeed(open: boolean): ChatFeed {
  const source = useLeagueSource();
  const card = useActiveLeague();
  const real = source === 'api' && card !== null;
  const simFeed = useSimFeed(source === 'sim');
  const apiFeed = useApiFeed(real ? card.league.id : null, open, 'league');
  if (real) return apiFeed;
  if (source === 'sim') return simFeed;
  return { ...simFeed, messages: [], state: 'loading' };
}

// TODO(I9): mask the practice league's simulated chat, and give its commissioner Hide.
function useSimFeed(active: boolean): ChatFeed {
  const sim = useSim();
  const { state, version } = sim;
  const { session } = useSession();
  const myTeam = useMyTeam();
  // In the league I am my team's owner; the session name only covers a league I have no seat in.
  const me = myTeam?.ownerName || (session?.displayName ?? 'You');
  const color = myTeam?.colors.secondary ?? null;
  const messages = useMemo(
    () => (active ? chatView(state) : []),
    // `version` is the simulator's change counter: the state object is mutated in place.
    [active, state, version],
  );
  return useMemo<ChatFeed>(
    () => ({
      messages,
      state: 'ready',
      problem: null,
      async post(body, replyTo = null) {
        sim.postChat(me, body, color, replyTo);
        return { ok: true };
      },
      async sticker(sticker, replyTo = null) {
        sim.postChat(me, '', color, replyTo, sticker);
        return { ok: true };
      },
      async react(messageId, emoji, on) {
        // The simulator toggles; only ask it when that lands where `on` says.
        const m = messages.find((x) => x.id === messageId);
        const mine = m?.myReactions?.includes(emoji) ?? false;
        if (mine !== on) sim.reactChat(messageId, emoji);
        return { ok: true };
      },
    }),
    [messages, sim, me, color],
  );
}

/**
 * A conversation of mine in the active real league, while it is open in the
 * drawer (I8): asked as often as an open drawer asks, and not at all with
 * `channel` null.
 */
export function useConversationFeed(channel: string | null): ChatFeed {
  const card = useActiveLeague();
  const source = useLeagueSource();
  const leagueId = source === 'api' && card !== null ? card.league.id : null;
  return useApiFeed(channel === null ? null : leagueId, true, channel ?? 'league');
}

/**
 * The conversation with whoever manages `teamId`, found or begun (I8,
 * `POST /leagues/:id/chat/dms`).
 */
export async function startConversation(
  leagueId: string,
  teamId: string,
): Promise<{ ok: true; conversation: ConversationRef } | { ok: false; message: string }> {
  const reply = await send(
    'POST',
    `/leagues/${encodeURIComponent(leagueId)}/chat/dms`,
    { teamId },
    ConversationRefSchema,
  );
  if (reply.status !== 'ok')
    return { ok: false, message: messageOf(reply, 'That conversation could not be opened.') };
  return { ok: true, conversation: reply.data };
}

function useApiFeed(leagueId: string | null, open: boolean, channel: string): ChatFeed {
  // WHAT IS HELD IS ONE CHANNEL OF ONE LEAGUE'S: `scope` names both, so a
  // conversation's lines are never laid over another's.
  const scope = leagueId === null ? null : `${leagueId}/${channel}`;
  const [held, setHeld] = useState<{ scope: string; items: ChatMessage[] } | null>(null);
  const [lists, setLists] = useState<{
    scope: string;
    conversations: ConversationRef[];
    people: ChatPerson[] | null;
  } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const writes = useRef(0);
  const keys = useRef(new AttemptKeys());
  /** The last answer's mark, which the next ask for that channel sends as `since`. */
  const mark = useRef<{ scope: string; mark: string } | null>(null);
  const path = `/leagues/${leagueId}/chat/${encodeURIComponent(channel)}`;

  const keptName = `chat:${path}`;
  /** What the device kept for this channel, drawn while nothing is held for it. */
  const drawKept = useCallback(
    async (account: string | null) => {
      if (scope === null) return;
      const copy = await onDevice.find(account, keptName);
      const items = copy === null ? null : KeptItems.safeParse(copy.data);
      if (items === null || !items.success) return;
      setHeld((h) => (h !== null && h.scope === scope ? h : { scope, items: items.data }));
    },
    [scope, keptName],
  );

  const ask = useCallback(
    async (signal: AbortSignal, whole: boolean) => {
      if (leagueId === null || scope === null) return;
      const account = onDevice.account();
      for (let tries = 0; tries < 3; tries++) {
        const before = writes.current;
        const since = !whole && mark.current?.scope === scope ? mark.current.mark : null;
        const reply = await get(
          `${path}?limit=${PAGE}` + (since === null ? '' : `&since=${encodeURIComponent(since)}`),
          ChatPage,
          { signal },
        );
        if (signal.aborted) return;
        // A MARK THE SERVER WILL NOT HONOR is let go, and the page read whole.
        if (since !== null && reply.status === 'refused' && reply.code === 'invalid') {
          mark.current = null;
          continue;
        }
        if (reply.status !== 'ok') {
          setProblem(messageOf(reply, 'Chat could not be read.'));
          if (reply.status === 'refused' && GONE.has(reply.code)) onDevice.drop(account, keptName);
          else if (reply.status !== 'refused' || NOT_NOW.has(reply.code)) void drawKept(account);
          return;
        }
        if (writes.current !== before) continue;
        setProblem(null);
        const page = reply.data;
        mark.current = page.mark === undefined ? null : { scope, mark: page.mark };
        if (page.merge !== true) setHeld({ scope, items: [...page.items].reverse() });
        // Nothing changed, nothing drawn again.
        else if (page.items.length > 0)
          setHeld((h) => ({
            scope,
            items: layOver(h !== null && h.scope === scope ? h.items : [], page.items),
          }));
        const conversations = page.conversations;
        if (conversations !== undefined)
          setLists((l) => {
            const same = l !== null && l.scope === scope;
            const people = page.people ?? (same ? l.people : null);
            // Unchanged, the same object, so nothing draws again on a quiet poll.
            if (
              same &&
              l.people === people &&
              JSON.stringify(l.conversations) === JSON.stringify(conversations)
            )
              return l;
            return { scope, conversations, people };
          });
        return;
      }
    },
    [leagueId, scope, path, keptName, drawKept],
  );

  // EVERY CHANGE TO WHAT IS HELD IS KEPT, the newest page of it, for the
  // account it was read as.
  const account = onDevice.account();
  useEffect(() => {
    if (held === null || held.scope !== scope) return;
    onDevice.keep(account, keptName, {
      data: held.items.slice(-PAGE),
      asOf: held.items.at(-1)?.at ?? '',
    });
  }, [held, scope, keptName, account]);

  useEffect(() => {
    if (leagueId === null) return;
    const control = new AbortController();
    const now = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void ask(control.signal, false);
    };
    // Opening the drawer reads the page whole; shutting it asks what changed.
    if (!document.hidden) void ask(control.signal, open);
    const timer = window.setInterval(now, open ? CHAT_ASK_OPEN_MS : CHAT_ASK_SHUT_MS);
    document.addEventListener('visibilitychange', now);
    return () => {
      control.abort();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', now);
    };
  }, [leagueId, open, ask]);

  /** Put a message the server just answered with where it belongs. */
  const land = useCallback(
    (m: ChatMessage) => {
      if (scope === null) return;
      writes.current += 1;
      // A MARK BELONGS WITH THE LIST IT WAS LAID OVER: a message landing in a
      // league whose page has not come back starts that league's list, and
      // another league's mark kept beside it would, on going back there, be
      // laid over this one (the review of the polls). The next ask reads whole.
      if (mark.current !== null && mark.current.scope !== scope) mark.current = null;
      setHeld((h) => {
        const items = h !== null && h.scope === scope ? h.items : [];
        return { scope, items: layOver(items, [m]) };
      });
    },
    [scope],
  );

  // AN ANSWER FOR ANOTHER LEAGUE IS NOT AN ANSWER (`read.ts`): compared in
  // render, so switching league never shows the last one's talk for a beat.
  const mine = held !== null && held.scope === scope ? held.items : null;
  const listed = lists !== null && lists.scope === scope ? lists : null;

  /**
   * A post, words or a sticker, KEYED (B-150's rule): the same request sent
   * again after no answer is the first message, not a second one.
   */
  const said = useCallback(
    async (
      request:
        | { body: string; replyTo?: string }
        | { sticker: StickerId; replyTo?: string }
        | { share: ChatShare; body?: string; replyTo?: string },
    ): Promise<ChatOutcome> => {
      if (leagueId === null) return { ok: false, message: 'There is no league here.' };
      const key = keys.current.keyFor(request);
      const reply = await send('POST', path, request, ChatMessageSchema, {
        headers: { 'idempotency-key': key },
      });
      keys.current.settled(reply);
      if (reply.status !== 'ok')
        return { ok: false, message: messageOf(reply, 'That did not send.', UNREACHABLE) };
      land(reply.data);
      return { ok: true };
    },
    [leagueId, path, land],
  );

  return useMemo<ChatFeed>(
    () => ({
      messages: mine ?? [],
      state:
        mine === null ? (problem === null ? 'loading' : 'failed') : problem ? 'failed' : 'ready',
      problem,
      async post(body, replyTo = null) {
        // THE SAME WORDS ANSWERING ANOTHER LINE ARE ANOTHER MESSAGE: the key
        // is minted over both, as the server compares both.
        return said(replyTo === null ? { body } : { body, replyTo });
      },
      async sticker(sticker, replyTo = null) {
        return said(replyTo === null ? { sticker } : { sticker, replyTo });
      },
      async share(share, body, replyTo = null) {
        return said({
          share,
          ...(body === null ? {} : { body }),
          ...(replyTo === null ? {} : { replyTo }),
        });
      },
      async react(messageId, emoji, on) {
        if (leagueId === null) return { ok: false, message: 'There is no league here.' };
        const reply = await send(
          'POST',
          `${path}/${encodeURIComponent(messageId)}/react`,
          { emoji, on },
          ChatMessageSchema,
        );
        if (reply.status !== 'ok')
          return { ok: false, message: messageOf(reply, 'That reaction did not land.') };
        land(reply.data);
        return { ok: true };
      },
      async hide(messageId, hidden) {
        if (leagueId === null) return { ok: false, message: 'There is no league here.' };
        const reply = await send(
          'POST',
          `${path}/${encodeURIComponent(messageId)}/hide`,
          { hidden },
          ChatMessageSchema,
        );
        if (reply.status !== 'ok')
          return { ok: false, message: messageOf(reply, 'That did not change. Try again.') };
        land(reply.data);
        return { ok: true };
      },
      ...(channel === 'league' && leagueId !== null
        ? {
            // KEYED AS A POST IS: the same poll sent again after no answer is
            // the first one.
            async ask(poll: CreatePollInput) {
              const key = keys.current.keyFor({ poll });
              const reply = await send(
                'POST',
                `/leagues/${encodeURIComponent(leagueId)}/polls`,
                poll,
                ChatMessageSchema,
                { headers: { 'idempotency-key': key } },
              );
              keys.current.settled(reply);
              if (reply.status !== 'ok')
                return {
                  ok: false,
                  message: messageOf(reply, 'That poll did not go up.', UNREACHABLE),
                };
              land(reply.data);
              return { ok: true };
            },
            async vote(pollId: string, option: number | null) {
              const reply = await send(
                'POST',
                `/leagues/${encodeURIComponent(leagueId)}/polls/${encodeURIComponent(pollId)}/vote`,
                { option },
                ChatMessageSchema,
              );
              if (reply.status !== 'ok')
                return {
                  ok: false,
                  message: messageOf(reply, 'That vote did not count. Try again.'),
                };
              land(reply.data);
              // HOME'S WAITING LIST LOSES THE POLL: asked again, as after a move.
              setTimeout(() => movesChanged(leagueId), 0);
              return { ok: true };
            },
          }
        : {}),
      ...(listed === null
        ? {}
        : {
            conversations: listed.conversations,
            ...(listed.people ? { people: listed.people } : {}),
          }),
    }),
    [mine, problem, leagueId, land, path, listed, said, channel],
  );
}

/**
 * What changed, laid over what is held: by id, oldest first as the drawer
 * draws them (the page's order, turned round), and the newest page kept.
 */
export function layOver(held: ChatMessage[], changed: ChatMessage[]): ChatMessage[] {
  const byId = new Map(held.map((m) => [m.id, m]));
  for (const m of changed) byId.set(m.id, m);
  // A QUOTE READS ITS LINE AS HELD (C2): a line hidden, or shown again, since
  // its reply was read is quoted as it now reads. The server sends the
  // replies again too; this is the line this client already has.
  for (const m of changed)
    for (const [id, r] of byId)
      if (r.replyTo?.id === m.id && !r.replyTo.gone && r.replyTo.author !== null)
        byId.set(id, {
          ...r,
          replyTo: {
            id: m.id,
            author: r.replyTo.author,
            body: m.body,
            ...(m.hidden ? { hidden: true } : {}),
          },
        });
  const key = (m: ChatMessage) => `${m.at}|${m.id}`;
  const all = [...byId.values()].sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0));
  return all.length > PAGE ? all.slice(all.length - PAGE) : all;
}
